import React, { useState, useEffect } from 'react'
import { Card, Table, InputNumber, Select, Button, Tag, message, Modal, Typography } from 'antd'
import { CodeOutlined, StopOutlined } from '@ant-design/icons'
import { backend } from '../../api'

const { Title, Text } = Typography

const TYPE_OPTIONS = [
  { value: 'member', label: '会员' },
  { value: 'admin', label: '管理员' }
]
const LEVEL_OPTIONS = [
  { value: 'basic', label: '基础版' },
  { value: 'pro', label: '专业版' },
  { value: 'enterprise', label: '企业版' },
  { value: 'ultimate', label: '旗舰版' }
]
const STATUS_MAP = {
  active: { color: 'green', label: '未使用' },
  used: { color: 'blue', label: '已使用' },
  revoked: { color: 'red', label: '已作废' }
}
const CODE_TYPE_MAP = { member: '会员', admin: '管理员' }
const LEVEL_COLORS = { basic: 'default', pro: 'gold', enterprise: 'purple', ultimate: 'cyan' }

export default function ActivationCodeManage() {
  const [codes, setCodes] = useState([])
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generatedCodes, setGeneratedCodes] = useState([])
  const [generatedVisible, setGeneratedVisible] = useState(false)

  const [count, setCount] = useState(1)
  const [codeType, setCodeType] = useState('member')
  const [codeLevel, setCodeLevel] = useState('basic')
  const [validDays, setValidDays] = useState(30)
  const [maxDevices, setMaxDevices] = useState(3)

  const fetchCodes = async () => {
    setLoading(true)
    try {
      const res = await backend.adminGetCodes()
      if (res.codes) {
        setCodes(res.codes)
      } else if (Array.isArray(res)) {
        setCodes(res)
      } else {
        setCodes([])
      }
    } catch (err) {
      message.error('获取激活码列表失败: ' + (err.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchCodes() }, [])

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const res = await backend.adminGenerateCodes({
        count,
        type: codeType,
        level: codeLevel,
        valid_days: validDays,
        max_devices: maxDevices
      })
      if (res.success || res.codes) {
        const list = res.codes || res.data?.codes || res.data || []
        setGeneratedCodes(list)
        setGeneratedVisible(true)
        message.success(`成功生成 ${list.length || count} 个激活码`)
        fetchCodes()
      } else {
        message.error(res.message || '生成失败')
      }
    } catch (err) {
      message.error('生成失败: ' + (err.message || '未知错误'))
    } finally {
      setGenerating(false)
    }
  }

  const handleRevoke = (record) => {
    Modal.confirm({
      title: '确认作废',
      content: `确定要作废激活码 ${record.code} 吗？`,
      okText: '作废',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await backend.adminRevokeCode(record.id || record.code)
          if (res.success) {
            message.success('激活码已作废')
            fetchCodes()
          } else {
            message.error(res.message || '作废失败')
          }
        } catch (err) {
          message.error('作废失败: ' + (err.message || '未知错误'))
        }
      }
    })
  }

  const codeColumns = [
    { title: '激活码', dataIndex: 'code', key: 'code', width: 200 },
    {
      title: '类型', dataIndex: 'type', key: 'type', width: 80,
      render: v => <Tag>{CODE_TYPE_MAP[v] || v}</Tag>
    },
    {
      title: '等级', dataIndex: 'level', key: 'level', width: 80,
      render: v => {
        const label = LEVEL_OPTIONS.find(o => o.value === v)?.label || v
        return <Tag color={LEVEL_COLORS[v] || 'default'}>{label}</Tag>
      }
    },
    {
      title: '有效天数', dataIndex: 'valid_days', key: 'valid_days', width: 80,
      render: (v, record) => {
        const days = v || record.days
        return days ? `${days} 天` : '-'
      }
    },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 80,
      render: v => {
        const cfg = STATUS_MAP[v] || { color: 'default', label: v }
        return <Tag color={cfg.color}>{cfg.label}</Tag>
      }
    },
    {
      title: '使用者', dataIndex: 'used_by_username', key: 'used_by_username', width: 100,
      render: (v, record) => v || record.used_by_name || '-'
    },
    {
      title: '使用时间', dataIndex: 'used_at', key: 'used_at', width: 120,
      render: v => v ? new Date(v).toLocaleString() : '-'
    },
    {
      title: '操作', key: 'actions', width: 80, fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<StopOutlined />}
          disabled={record.status !== 'active'}
          onClick={() => handleRevoke(record)}
        >
          作废
        </Button>
      )
    }
  ]

  const generatedColumns = [
    { title: '激活码', dataIndex: 'code', key: 'code' },
    {
      title: '类型', dataIndex: 'type', key: 'type', width: 80,
      render: v => <Tag>{CODE_TYPE_MAP[v] || v}</Tag>
    },
    {
      title: '等级', dataIndex: 'level', key: 'level', width: 80,
      render: v => {
        const label = LEVEL_OPTIONS.find(o => o.value === v)?.label || v
        return <Tag color={LEVEL_COLORS[v] || 'default'}>{label}</Tag>
      }
    },
    { title: '有效天数', dataIndex: 'valid_days', key: 'valid_days', width: 80,
      render: (v, record) => {
        const days = v || record.days
        return days ? `${days} 天` : '-'
      } }
  ]

  return (
    <div>
      <Title level={4}><CodeOutlined /> 激活码管理</Title>

      <Card title="生成激活码" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>生成数量</Text>
            <InputNumber min={1} max={100} value={count} onChange={setCount} style={{ width: 120 }} />
          </div>
          <div>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>类型</Text>
            <Select value={codeType} onChange={setCodeType} options={TYPE_OPTIONS} style={{ width: 120 }} />
          </div>
          <div>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>会员等级</Text>
            <Select value={codeLevel} onChange={setCodeLevel} options={LEVEL_OPTIONS} style={{ width: 130 }} />
          </div>
          <div>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>有效天数</Text>
            <InputNumber min={1} max={3650} value={validDays} onChange={setValidDays} style={{ width: 120 }} />
          </div>
          <div>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>设备数上限</Text>
            <InputNumber min={1} max={100} value={maxDevices} onChange={setMaxDevices} style={{ width: 120 }} />
          </div>
        </div>
        <Button type="primary" icon={<CodeOutlined />} loading={generating} onClick={handleGenerate}>
          生成激活码
        </Button>
      </Card>

      <Card title="激活码列表">
        <Table
          rowKey={r => r.id || r.code}
          columns={codeColumns}
          dataSource={codes}
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `共 ${t} 条` }}
        />
      </Card>

      <Modal
        title="已生成的激活码"
        open={generatedVisible}
        onCancel={() => setGeneratedVisible(false)}
        footer={<Button onClick={() => setGeneratedVisible(false)}>关闭</Button>}
        width={700}
      >
        <Table
          rowKey="code"
          columns={generatedColumns}
          dataSource={generatedCodes}
          pagination={false}
          size="small"
        />
      </Modal>
    </div>
  )
}
